import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';

import API from '../../services/api';
import SellOutBaseProductPicker from './SellOutBaseProductPicker';
import {
  numberValue,
  productLabel,
  variantModeLabel,
  variantModeOptions,
} from './selloutUtils';

const makeLine = () => ({
  client_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  expected_product: null,
  expected_product_id: null,
  expected_format_id: null,
  expected_variant_mode: 'exact',
  expected_qty: '',
  comment: '',
  base_products_payload: [],
});

function ExpectedProductAutocomplete({ line, onChange, disabled }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState(line.expected_product ? [line.expected_product] : []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = input.trim();
    if (query.length < 2) {
      setOptions(line.expected_product ? [line.expected_product] : []);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let mounted = true;
    setLoading(true);

    API.get('product/', {
      params: { search: query, limit: 12, active: true },
      signal: controller.signal,
    })
      .then(({ data }) => {
        if (!mounted) return;
        const rows = Array.isArray(data) ? data : data?.results || [];
        const merged = line.expected_product
          ? [line.expected_product, ...rows.filter((row) => row?.id !== line.expected_product?.id)]
          : rows;
        setOptions(merged);
      })
      .catch((error) => {
        if (!mounted) return;
        if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return;
        }
        setOptions(line.expected_product ? [line.expected_product] : []);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [input, line.expected_product]);

  return (
    <Autocomplete
      value={line.expected_product}
      options={options}
      loading={loading}
      disabled={disabled}
      onInputChange={(_, value) => setInput(value)}
      onChange={(_, product) => {
        onChange({
          expected_product: product || null,
          expected_product_id: product?.id || null,
          expected_format_id: product?.prformat?.id || null,
        });
      }}
      getOptionLabel={productLabel}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => (
        <TextField {...params} size="small" label="Producto esperado" />
      )}
    />
  );
}

export { makeLine as makeSellOutPromiseLine };

export default function SellOutPromiseLineEditor({
  lines,
  onChange,
  selectedOrders,
  errors,
}) {
  const totalExpected = useMemo(
    () => (lines || []).reduce((sum, line) => sum + numberValue(line.expected_qty), 0),
    [lines]
  );

  const updateLine = (clientId, patch) => {
    onChange((lines || []).map((line) => (
      line.client_id === clientId ? { ...line, ...patch } : line
    )));
  };

  const addLine = () => onChange([...(lines || []), makeLine()]);
  const removeLine = (clientId) => onChange((lines || []).filter((line) => line.client_id !== clientId));

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          Lineas de control
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Total esperado: {totalExpected}
          </Typography>
          <Button variant="contained" startIcon={<Plus size={17} />} onClick={addLine}>
            Agregar linea
          </Button>
        </Stack>
      </Stack>

      {(lines || []).map((line, index) => {
        const pendingMode = line.expected_variant_mode === 'pending';
        const lineErrors = errors?.[line.client_id] || [];
        const baseTotal = (line.base_products_payload || []).reduce(
          (sum, row) => sum + numberValue(row.quantity_base),
          0
        );

        return (
          <Accordion key={line.client_id} defaultExpanded={index === 0} disableGutters>
            <AccordionSummary expandIcon={<ChevronDown size={18} />}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 82 }}>
                  Linea {index + 1}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {line.expected_product ? productLabel(line.expected_product) : variantModeLabel(line.expected_variant_mode)}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Base {baseTotal} / Esperada {numberValue(line.expected_qty)}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {lineErrors.length > 0 ? (
                  <Box sx={{ color: 'error.main', fontSize: 13 }}>
                    {lineErrors.join(' ')}
                  </Box>
                ) : null}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Modo de variante"
                    value={line.expected_variant_mode}
                    onChange={(event) => {
                      const mode = event.target.value;
                      updateLine(line.client_id, {
                        expected_variant_mode: mode,
                        ...(mode === 'pending'
                          ? { expected_product: null, expected_product_id: null, expected_format_id: null }
                          : {}),
                      });
                    }}
                    sx={{ minWidth: { md: 210 } }}
                  >
                    {variantModeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <ExpectedProductAutocomplete
                      line={line}
                      disabled={pendingMode}
                      onChange={(patch) => updateLine(line.client_id, patch)}
                    />
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Cantidad esperada"
                    value={line.expected_qty}
                    onChange={(event) => updateLine(line.client_id, { expected_qty: event.target.value })}
                    inputProps={{ min: 1 }}
                    sx={{ width: { md: 170 } }}
                  />
                </Stack>

                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  label="Comentario"
                  value={line.comment || ''}
                  onChange={(event) => updateLine(line.client_id, { comment: event.target.value })}
                />

                <Divider />

                <SellOutBaseProductPicker
                  selectedOrders={selectedOrders}
                  value={line.base_products_payload || []}
                  onChange={(baseProducts) => updateLine(line.client_id, { base_products_payload: baseProducts })}
                />

                <Stack direction="row" justifyContent="flex-end">
                  <Tooltip title="Eliminar linea">
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => removeLine(line.client_id)}
                        disabled={(lines || []).length <= 1}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
